/**
 * PlansPage — catalog of all plan cycles with states.
 *
 * Nav position: directly under Live Dashboard.
 * Entry point for creating plans (from Item Pool, Collection, or published priorities).
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, Star } from 'lucide-react';
import { EntityListPage } from '../../components/collection/EntityListPage';
import { CreatePlanModal } from './CreatePlanModal';
import { listPlanCycles, setPrimaryPlanCycle } from '../../lib/planningService';
import { planScenarioReadyMessage } from '../../lib/planning/planScenarioSetup';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import type { PlanCycle, PlanCycleStatus } from '../../types/planning';
import type { TableColumnWithRender } from '../../components/collection/types';

type PlanSourceType = 'item_pool' | 'collection' | 'published_priority_list';

function parsePlanSource(raw: string | null): PlanSourceType | undefined {
  if (raw === 'item_pool' || raw === 'collection' || raw === 'published_priority_list') return raw;
  return undefined;
}

// ─── State badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<PlanCycleStatus, { bg: string; text: string; label: string }> = {
  draft:      { bg: 'bg-[var(--hover-tint)]',    text: 'text-[var(--text-sec)]',   label: 'Draft' },
  active:     { bg: 'bg-[var(--green-dim)]',    text: 'text-[var(--green)] dark:text-[var(--text-ter)]',  label: 'Active' },
  closed:     { bg: 'bg-[var(--hover-tint)]',   text: 'text-[var(--text-ter)]',  label: 'Closed' },
  superseded: { bg: 'bg-[var(--mustard-dim)]',    text: 'text-[var(--mustard)]',  label: 'Superseded' },
};

function StatusBadge({ status }: { status: PlanCycleStatus }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5  text-[11px] font-semibold ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

// ─── Source badge ─────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, string> = {
  published_priority_list: 'Priority List',
  item_pool:               'Item Pool',
  collection:              'Collection',
  provisional_plan:        'Provisional',
  previous_carry_forward:  'Carry Forward',
};

function SourceBadge({ inputType }: { inputType: string }) {
  const label = SOURCE_LABELS[inputType] ?? inputType;
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
      {label}
    </span>
  );
}

// ─── Table columns ────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { field: 'name'       as const, label: 'Name' },
  { field: 'status'     as const, label: 'Status' },
  { field: 'created_at' as const, label: 'Created' },
];

const FACETS = [
  {
    field: 'status' as const,
    label: 'Status',
    options: Object.entries(STATUS_STYLES).map(([v, s]) => ({ value: v, label: s.label })),
    type: 'multi' as const,
  },
  {
    field: 'input_type' as const,
    label: 'Source',
    options: Object.entries(SOURCE_LABELS).map(([v, l]) => ({ value: v, label: l })),
    type: 'multi' as const,
  },
];

// ─── Page ────────────────────────────────────────────────────────────────────

export default function PlansPage() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [plans, setPlans] = useState<PlanCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null);
  // set_primary_plan_cycle takes a workspace admin or owner (migration 735,
  // p_require_admin). Admins hold every permission action on the client, so
  // admin.settings stands for that arm. Set primary is drawn only for them (§2.8).
  const mayChoosePrimary = useUserStore((s) => s.can('admin.settings'));

  const handoff = useMemo(() => {
    const create = searchParams.get('create') === '1';
    if (!create) return null;
    return {
      source: parsePlanSource(searchParams.get('inputType')),
      inputId: searchParams.get('inputId') ?? undefined,
      name: searchParams.get('suggestedName') ?? undefined,
    };
  }, [searchParams]);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await listPlanCycles({ workspaceId });
    setPlans(data);
    if (err) setError(err);
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { void load(); }, [load]);

  // Cycle-report "Open in Planning" lands here with ?create=1&inputType=…
  useEffect(() => {
    if (handoff) setCreating(true);
  }, [handoff]);

  const clearHandoffParams = () => {
    if (!handoff) return;
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    next.delete('inputType');
    next.delete('inputId');
    next.delete('suggestedName');
    setSearchParams(next, { replace: true });
  };

  const handleSetPrimary = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSettingPrimaryId(id);
    const { error: err } = await setPrimaryPlanCycle(id);
    setSettingPrimaryId(null);
    if (err) {
      setError(err);
      return;
    }
    await load();
  };

  const tableColumns: TableColumnWithRender<PlanCycle>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      minWidth: 260,
      render: (c) => (
        <span className="flex items-center gap-2 min-w-0 w-full">
          <span className="block truncate font-semibold text-[var(--text-pri)]" title={c.name}>
            {c.name}
          </span>
          {c.is_primary && (
            <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700">
              <Star className="h-3 w-3 fill-current" />
              Primary
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      minWidth: 100,
      render: (c) => <StatusBadge status={c.status} />,
    },
    {
      key: 'input_type',
      label: 'Source',
      minWidth: 120,
      render: (c) => <SourceBadge inputType={c.input_type} />,
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      minWidth: 110,
      render: (c) => (
        <span className="text-sm text-[var(--text-ter)]">
          {new Date(c.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'id',
      label: '',
      minWidth: 120,
      render: (c) =>
        c.is_primary || !mayChoosePrimary ? null : (
          <button
            type="button"
            onClick={(e) => void handleSetPrimary(e, c.id)}
            disabled={settingPrimaryId === c.id}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-60"
          >
            <Star className="h-3.5 w-3.5" />
            {settingPrimaryId === c.id ? 'Setting…' : 'Set primary'}
          </button>
        ),
    },
  ];

  return (
    <>
      <EntityListPage<PlanCycle, 'name' | 'status' | 'created_at'>
        items={plans}
        loading={loading}
        getId={(c) => c.id}
        title="Plan of Record catalog"
        subtitle="All plan cycles in this workspace. One is primary — Plan of Record entry opens that overview (initiatives, health, next right work)."
        icon={<ClipboardList className="h-4 w-4 text-[var(--text-ter)]" />}
        sortOptions={SORT_OPTIONS}
        defaultSort={{ field: 'created_at', direction: 'desc' }}
        facets={FACETS}
        searchFields={['name'] as (keyof PlanCycle)[]}
        tableColumns={tableColumns}
        renderCard={(c, { selected }) => (
          <div className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-semibold text-[var(--text-pri)] truncate">{c.name}</p>
              {c.is_primary && (
                <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700">
                  <Star className="h-3 w-3 fill-current" />
                  Primary
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={c.status} />
              <SourceBadge inputType={c.input_type} />
            </div>
            {!c.is_primary && mayChoosePrimary && (
              <button
                type="button"
                onClick={(e) => void handleSetPrimary(e, c.id)}
                className="mt-2 text-xs font-medium text-primary-600 hover:text-primary-800"
              >
                Set as primary
              </button>
            )}
            {selected && <p className="text-xs text-[var(--text-ter)] mt-2">Selected</p>}
          </div>
        )}
        onCreate={() => setCreating(true)}
        createLabel="New Plan"
        emptyTitle="No Plan of Record yet"
        emptyDescription="Create your first plan from a priority list, item pool, or collection."
        emptyAction={{ label: 'New Plan', onClick: () => setCreating(true) }}
        error={error ?? undefined}
        itemLabel="plan"
        onSelectionChange={(id) => { if (id) navigate(`/planning/plans/${id}`); }}
        onItemClick={(c) => navigate(`/planning/plans/${c.id}`)}
        detailPanel={false}
        className="h-full min-h-0"
        shellClassName="h-full"
      />

      <CreatePlanModal
        isOpen={creating}
        onClose={() => {
          setCreating(false);
          clearHandoffParams();
        }}
        onCreated={(id, opts) => {
          clearHandoffParams();
          void load();
          // BREAK 2: when the engine scenario/scope/activation all completed,
          // land on the engine view with a short confirmation instead of the
          // plain plan detail page — the plan is otherwise a dead end that
          // still needs manual setup in ScenarioList / the scope picker.
          if (opts?.scenarioReady) {
            navigate('/planning/engine', {
              state: { setupConfirmation: planScenarioReadyMessage(opts.scopeName ?? 'the source list') },
            });
          } else {
            navigate(`/planning/plans/${id}`);
          }
        }}
        initialSource={handoff?.source}
        initialInputId={handoff?.inputId}
        initialName={handoff?.name}
      />
    </>
  );
}
